// Selection keys are presentation identities, never cloud conversation IDs.
export const isNativeCollectionChatKey = id => typeof id === 'string' && id.startsWith('mac:');
export function nativeCollectionChatKey(projectId, sessionId) { return `mac:${projectId}/${sessionId}`; }

export function partitionCollectionChatSelection(ids, cloudIds, nativeRows) {
  const cloud = new Set((cloudIds || []).filter(id => typeof id === 'string' && !isNativeCollectionChatKey(id) && !id.startsWith('ses_')));
  const native = new Map((nativeRows || []).map(row => [row.id, row]));
  const selected = [...new Set(ids || [])], cloudSelection = [], nativeSelection = [];
  for (const id of selected) {
    if (isNativeCollectionChatKey(id)) {
      const row = native.get(id);
      if (!row || row.execution?.type !== 'mac-opencode' || id !== nativeCollectionChatKey(row.projectId, row.localSessionId))
        throw new Error('A selected chat changed. Reload the collection before continuing.');
      nativeSelection.push(row);
    } else if (cloud.has(id)) cloudSelection.push(id);
    else throw new Error('A selected chat is no longer in this collection.');
  }
  return { cloudIds: cloudSelection, nativeRows: nativeSelection };
}

export async function runCollectionChatBulk({ ids, cloudIds, nativeRows, action, targetCollectionId, cloudRun, nativeRun, isCurrent = () => true }) {
  const selected = partitionCollectionChatSelection(ids, cloudIds, nativeRows);
  const failedIds = [], errors = []; let successCount = 0;
  const check = () => { if (!isCurrent()) throw new Error('Your account or collection changed. Reopen it before continuing.'); };
  check();
  // Native CAS revisions advance after each move. Never parallelize this loop
  // or capture one expected revision for an entire selection.
  for (const row of selected.nativeRows) {
    check();
    try {
      if (typeof nativeRun !== 'function') throw new Error('This Mac build cannot manage the selected chat.');
      await nativeRun(row, action, targetCollectionId); check(); successCount++;
    } catch (error) { check(); failedIds.push(row.id); errors.push(error.message); }
  }
  if (selected.cloudIds.length) {
    check();
    try {
      const result = await cloudRun(selected.cloudIds); check();
      if (!result || !Number.isInteger(result.successCount) || !Array.isArray(result.failedIds) ||
          result.failedIds.some(id => !selected.cloudIds.includes(id)) || new Set(result.failedIds).size !== result.failedIds.length ||
          result.successCount !== selected.cloudIds.length - result.failedIds.length) throw new Error('The service did not confirm every selected chat action.');
      successCount += result.successCount; failedIds.push(...result.failedIds);
      if (result.error?.message) errors.push(result.error.message);
    } catch (error) { check(); failedIds.push(...selected.cloudIds); errors.push(error.message); }
  }
  check(); return { successCount, failedIds, errors };
}

export function requireCollectionBulkSuccess(result) {
  if (result.failedIds.length) {
    const total = result.successCount + result.failedIds.length;
    throw new Error(`${result.successCount} of ${total} chat actions completed. ${result.failedIds.length} failed. ${result.errors[0] || 'Reload the collection and retry the remaining chats.'}`);
  }
  return result;
}
